import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText, AlertTriangle, Heart, Users, BookOpen, Scale } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function VilkaarOgAnsvar() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <LanguageSwitcher currentLang={i18n.language} onChangeLang={changeLanguage} />
            <Link to="/" className="p-1 border-2 border-black rounded-md hover:bg-black/5 transition-colors">
              <img 
                src="https://customer-assets.emergentagent.com/job_mindairy-site/artifacts/6hwqvtsj_mindary%20logo.jpeg" 
                alt="mindary logo" 
                className="w-10 h-10 object-contain"
              />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 mb-6">
              <FileText className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold text-foreground tracking-tight mb-4" data-testid="terms-title">
              {t('terms.title')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('terms.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Main Purpose Card */}
            <Card className="bg-emerald-50 border-emerald-100">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-emerald-500 flex items-center justify-center">
                    <Heart className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-semibold text-foreground">
                      {t('terms.purpose')}
                    </h2>
                    <p className="text-muted-foreground">{t('terms.purposeSubtitle')}</p>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {t('terms.purposeDesc1')}
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {t('terms.purposeDesc2')}
                </p>
              </CardContent>
            </Card>

            {/* Important Disclaimer */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-amber-500 flex items-center justify-center">
                    <AlertTriangle className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-semibold text-foreground">
                      {t('terms.disclaimer')}
                    </h2>
                    <p className="text-muted-foreground">{t('terms.disclaimerSubtitle')}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <p className="text-muted-foreground leading-relaxed">
                    <strong>{t('terms.disclaimerIntro')}</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                    <li>{t('terms.disclaimerPoint1')}</li>
                    <li>{t('terms.disclaimerPoint2')}</li>
                    <li>{t('terms.disclaimerPoint3')}</li>
                    <li>{t('terms.disclaimerPoint4')}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Professional Help Card */}
            <Card className="bg-blue-50 border-blue-100">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-blue-500 flex items-center justify-center">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-semibold text-foreground">
                      {t('terms.professionalHelp')}
                    </h2>
                    <p className="text-muted-foreground">{t('terms.professionalHelpSubtitle')}</p>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {t('terms.professionalHelpDesc1')}
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {t('terms.professionalHelpDesc2')}
                </p>
              </CardContent>
            </Card>

            {/* How to use */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-indigo-50 border-indigo-100">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-indigo-500 flex items-center justify-center mb-4">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{t('terms.recommendedUse')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('terms.recommendedUseDesc')}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-rose-50 border-rose-100">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-rose-500 flex items-center justify-center mb-4">
                    <Scale className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{t('terms.yourResponsibility')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('terms.yourResponsibilityDesc')}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Emergency Contact */}
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-foreground text-center mb-4">{t('terms.emergencyTitle')}</h3>
                <p className="text-center text-muted-foreground mb-6">
                  {t('terms.emergencySubtitle')}
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">Norge</p>
                    <p className="text-muted-foreground">Mental Helse: <strong>116 123</strong></p>
                    <p className="text-muted-foreground">Legevakt: 116 117</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">Sverige</p>
                    <p className="text-muted-foreground">Mind Självmordslinjen: <strong>90101</strong></p>
                    <p className="text-muted-foreground">Nødnummer: 112</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">Danmark</p>
                    <p className="text-muted-foreground">Livslinien: <strong>70 201 201</strong></p>
                    <p className="text-muted-foreground">Psykiatrifonden: 39 25 25 25</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">Suomi (Finland)</p>
                    <p className="text-muted-foreground">MIELI Kriisipuhelin: <strong>09 2525 0111</strong></p>
                    <p className="text-muted-foreground">Hätänumero: 112</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">UK (English)</p>
                    <p className="text-muted-foreground">Samaritans: <strong>116 123</strong></p>
                    <p className="text-muted-foreground">CALM: 0800 58 58 58</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">USA (English)</p>
                    <p className="text-muted-foreground">Suicide & Crisis: <strong>988</strong></p>
                    <p className="text-muted-foreground">SAMHSA: 1-800-662-4357</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">Deutschland</p>
                    <p className="text-muted-foreground">Telefonseelsorge: <strong>0800 111 0 111</strong></p>
                    <p className="text-muted-foreground">Notruf: 112</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">France</p>
                    <p className="text-muted-foreground">Suicide Écoute: <strong>01 45 39 40 00</strong></p>
                    <p className="text-muted-foreground">Urgences: 112</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">España</p>
                    <p className="text-muted-foreground">Teléfono de la Esperanza: <strong>717 003 717</strong></p>
                    <p className="text-muted-foreground">Emergencias: 112</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">Italia</p>
                    <p className="text-muted-foreground">Telefono Amico: <strong>800 86 00 22</strong></p>
                    <p className="text-muted-foreground">Emergenze: 112</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">Polska</p>
                    <p className="text-muted-foreground">ITAKA Kryzysowy: <strong>800 70 2222</strong></p>
                    <p className="text-muted-foreground">Telefon zaufania: 116 123</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-semibold text-foreground">Россия (Russia)</p>
                    <p className="text-muted-foreground">Телефон доверия: <strong>8-800-2000-122</strong></p>
                    <p className="text-muted-foreground">Экстренная помощь: 112</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <div className="text-center pt-8">
              <p className="text-muted-foreground mb-4">
                {t('terms.questions')}
              </p>
              <Link to="/kontakt" className="text-amber-600 hover:text-amber-700 font-medium">
                {t('terms.contactUs')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
